/**
 * 이상한 방 - 매치 호스트 (이벤트 큐 · 틱 선적용 · CPU 드라이버 · 수신자별 투영)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 9.3절(CPU 이벤트는 사람과 같은 큐) · 11.3절(서버가 쥐는 것) · 12절(동시 클릭)
 *            로드맵 모델 2.1절(절대 시각 · 틱 선적용 · 병합 불변) · 2.5절(호스트가 쥐는 것) · 2.6절(CPU) · 2.4절(와이어)
 *            로드맵 7절 M1 기록(`createMatch(roomCode, seed, host, config, deps)` · `cpuDecisionsDue` · `botStreamSeed`)
 *
 * ## 루프백과 서버가 같이 쓰는 유일한 호스트다
 *
 * 매치 상태의 원본은 **여기만** 쥔다. 브라우저 안의 봇 판(`loopbackTransport.ts`)과 소켓 너머의 방(M6 `server/`)이
 * 같은 이 함수를 부르므로, 봇 판이 돌면 서버 판의 규칙도 돈다(브리프 "CPU 상대 멀티가 첫 마일스톤의 검증 수단").
 * 그래서 이 파일은 `setInterval`도 `Date.now()`도 모른다 - 바깥이 `tick(atMs)`를 부른다. 루프백에서는 프레임
 * 델타의 누적이, 서버에서는 `server/src/clock.ts`가 그 시각이다. 시계를 읽는 파일은 저장소에 둘뿐이다(로드맵 부록).
 *
 * ## 큐는 하나, 도착 순 = 적용 순
 *
 * 사람의 `act`와 CPU의 판단이 **같은 큐**에 들어간다(9.3절 "새 코드 경로가 생기지 않는다"). 리듀서는 누가 넣었는지
 * 모른다. 그래서 "동시 클릭"이 없다(12절) - 먼저 들어온 발이 `found`에 굳고 늦은 발은 `already-found`다.
 *
 * ## 사람 이벤트 직전에 `tick(atMs)`를 선적용한다
 *
 * 사격이 마감 0.1초 뒤에 도착했으면 그 사격은 리빌에 도착한 것이다. 시계를 먼저 그 시각으로 밀어야 리듀서의
 * 페이즈 게이트가 맞는 답을 낸다. 병합 불변(`tick(t1); tick(t2) ≡ tick(t2)`, 한 구간 안에서)이 이 선적용을 공짜로
 * 만든다 - 틱이 잘게 쪼개져도 결과가 같다. 체류 → 쿨타임 경계를 넘는 병합만 예외인데(M0 기록), 호스트 틱 간격만큼
 * 쿨타임이 늦게 시작할 수 있다는 것이고 **허용 오차로 둔다** - 초과분을 깎으려면 리듀서에 규칙이 하나 늘고, 그 규칙은
 * 싱글에 없는 것이다(패리티가 깨진다).
 *
 * ## CPU 드라이버는 판단 경계의 커서를 따로 든다
 *
 * `cpuDecisionsDue(before, after, profile)`의 `before`는 "직전 틱의 경과"가 아니라 **CPU를 마지막으로 돌린 경과**다.
 * 둘이 갈리는 자리가 사람의 선적용이다 - 사람 이벤트가 시계를 6.0초로 밀고 그 뒤 `tick(6.0초)`가 오면 dt가 0이라
 * "직전 틱 기준"으로는 봇이 6초 경계를 영원히 놓친다. 커서는 페이즈가 바뀌는 순간 0으로 돌아간다(경과가 0으로 돌아가므로).
 * 한 틱에 넘은 경계가 여럿이면 **경계 시각 순**으로 판단한다(`cpuDecisionsBetween`) - 틱이 잘게 오든 크게 오든 적용 순서가
 * 같아야 "같은 `?seed=`면 봇이 같은 것을 쏜다"가 프레임 열에 기대지 않는다.
 * 봇의 난수 스트림은 `botStreamSeed(seed, id, round)` 하나를 라운드 시작에 만들어 라운드 내내 이어 쓴다 -
 * 같은 시드 + 같은 이벤트 열이면 같은 것을 쏜다(M1 규칙 7). `match.test.ts`의 `hostRound`가 이 파일의 참고 구현이다.
 *
 * ## `rooms`는 가시성이 바뀌는 자리에서만
 *
 * `roomsChanged` 효과가 났거나, 그 사람의 `view.rooms`(어느 방을 보여 줄 수 있는가 - `matchView.roomsFor`)가 마지막으로
 * 보낸 것과 다르면 보낸다. 후자가 있어야 사람 체인저의 바꾸기 진입(`draft`가 열린다)처럼 효과 없이 가시성만 바뀌는
 * 경계도 잡힌다. 효과 필터는 `to === 'all' | playerId`다 - 봇에게 간 효과(`shotResolved`)는 받을 클라이언트가 없어 버려진다.
 *
 * ## 거절 사유는 `unknown`이다
 *
 * 리듀서는 무시할 때 이유를 말하지 않는다(참조 동일). 여기서 "방장이 아니라서 · 그 페이즈가 아니라서"를 추론하면 그것이
 * 리듀서 게이트의 두 번째 사본이다. 뷰의 불리언이 이미 버튼을 막았을 것이므로 여기까지 온 것은 지연이나 위장이고,
 * 둘 다 사유가 필요한 대상이 아니다. `not-player-event`(호스트 이벤트 위장)만 여기서 가른다.
 *
 * `net/`은 `core`·`game/match`·`game` 리프만 본다(`boundary.test.ts`). 서버(M6)가 이 파일을 그대로 import 한다.
 */

import { createRng, type Rng } from '../core/rng'
import { encodeRoom } from '../core/roomCodec'
import type { PlayerId } from '../core/types'
import { botStreamSeed, cpuDecisionsDue } from '../game/match/cpu'
import { phaseDurationMs } from '../game/match/matchPhases'
import { seatsInOrder } from '../game/match/matchSelectors'
import { createMatch, defaultMatchConfig, reduceMatch } from '../game/match/matchState'
import { toClientView, type ClientView, type RoomsVisibility } from '../game/match/matchView'
import type {
  JoinEvent,
  MatchConfig,
  MatchDeps,
  MatchEffectBody,
  MatchEvent,
  MatchState,
  MatchStep,
} from '../game/match/matchTypes'
import { isPlayerEventType, type ClientMessage, type RoomsPayload, type ServerMessage } from './protocol'

/* --- 상수 ------------------------------------------------------------------------ */

/**
 * `clock` 메시지 간격(ms) = 2Hz. 전송 빈도라 라운드 규칙이 아니고, 그래서 `tuning.ts`가 아니다. 시계 보간에 필요한 것은
 * 초당 두 번의 진실이면 충분하다 - 그 사이는 클라이언트가 프레임 델타로 잇는다.
 */
export const CLOCK_INTERVAL_MS = 500

/** 첫 메시지의 `seq`. 클라이언트 문(`createSeqGate`)의 기준 0보다 크다. */
const FIRST_SEQ = 1

/* --- 타입 ------------------------------------------------------------------------ */

export type HostSeat = JoinEvent['player']

export type SendFn = (to: PlayerId, msg: ServerMessage) => void

export type MatchHostOptions = Readonly<{
  roomCode: string
  seed: string
  /** `tuning`은 여기 것이다. 따로 받으면 리듀서와 투영이 다른 수치를 보는 길이 생긴다. */
  deps: MatchDeps
  /** 생략하면 `defaultMatchConfig(deps.tuning)`. */
  config?: MatchConfig
  /** 좌석의 재접속 토큰. 루프백은 엔트로피가 필요 없어 기본값이 좌석 id에서 결정론적으로 나온다. 서버(M6)가 갈아 끼운다. */
  tokenFor?: (playerId: PlayerId) => string
}>

export type MatchHost = Readonly<{
  roomCode: string
  /** 좌석을 앉힌다. 첫 좌석이 매치를 만들고 방장이 된다. 앉은 사람에게 `welcome`, 나머지에게 `step`. */
  join: (player: HostSeat, magazines: number) => void
  /** 끊겼다(로비면 좌석이 사라지고, 진행 중이면 `connected: false`로 남는다). `atMs`가 있으면 그 시각까지 시계를 먼저 민다. */
  leave: (playerId: PlayerId, atMs?: number) => void
  /**
   * 돌아왔다. 돌아온 사람은 **`welcome`**(뷰 + 방 둘 + 토큰)으로 전부 다시 받는다 - 끊긴 동안 페이즈가 몇 번 넘어갔는지 모르고,
   * 새 소켓은 `seq` 기준도 없다. 나머지에게는 `connected`가 바뀐 `step`. 이미 접속 중인 좌석(같은 토큰의 새 소켓)이어도 `welcome`은
   * 간다 - 새 소켓이 빈손인 것은 같다. `atMs`가 있으면 그 시각까지 시계를 먼저 민다(스냅샷의 시계가 현재여야 한다).
   */
  reconnect: (playerId: PlayerId, atMs?: number) => void
  /** 클라이언트 메시지 하나. `atMs`는 이 메시지가 도착한 호스트 시각 - 사람 이벤트 직전의 선적용이 이 값으로 간다. */
  receive: (from: PlayerId, msg: ClientMessage, atMs: number) => void
  /** 시간이 흘렀다. 틱 적용 → CPU 드라이버 → 2Hz `clock`. 바깥(프레임 루프 · 서버 시계)이 부른다. */
  tick: (atMs: number) => void
  onSend: (send: SendFn) => () => void
  /** 디버그·테스트용 원본. 매치가 아직 없으면(첫 좌석 전) `null`. 클라이언트 코드가 이것을 읽으면 그것이 곧 규칙 유출이다. */
  state: () => MatchState | null
}>

/** 큐의 한 줄. `origin`이 있으면 사람의 `act`다 - 무시되면 그 `cseq`로 거절을 돌려준다. */
type Queued = Readonly<{
  ev: MatchEvent
  /** 사람 이벤트의 도착 시각. 적용 직전에 시계를 여기까지 민다. CPU 이벤트는 이미 현재 시각이라 없다. */
  atMs?: number
  origin?: Readonly<{ from: PlayerId; cseq: number }>
}>

/* --- 조각 ------------------------------------------------------------------------ */

function sameVisibility(a: RoomsVisibility | undefined, b: RoomsVisibility): boolean {
  return a !== undefined && a.original === b.original && a.mutated === b.mutated
}

/**
 * 가시성 플래그를 방 데이터로. 어느 방을 줄 수 있는가는 투영의 결정이고(`matchView.roomsFor`), 여기는 그 결정을 싣는다.
 * `draft`는 **받는 사람의 작업대**에서 꺼낸다(M11) - 전원이 동시에 자기 구획을 고치므로 초안이 좌석마다 하나이고,
 * 여기서 남의 작업대를 꺼내면 그것이 곧 정답표 유출이다.
 */
function roomsPayload(state: MatchState, visibility: RoomsVisibility, to: PlayerId): RoomsPayload {
  const mutatedRoom =
    visibility.mutated === 'mutated' ? state.mutated : visibility.mutated === 'draft' ? state.desks?.[to]?.draft ?? null : null
  return {
    original: visibility.original ? encodeRoom(state.original) : null,
    mutated: mutatedRoom === null ? null : encodeRoom(mutatedRoom),
  }
}

/** 이웃이 판단하는 페이즈. 그 밖(로비·외우기·리빌·결과)에는 이웃이 할 일이 없다. */
function isCpuPhase(state: MatchState): boolean {
  return state.phase === 'hunt' || state.phase === 'change'
}

/** CPU 판단 하나의 예약 - 어느 경계 시각에 어느 좌석이. `order`는 좌석 순서(같은 시각의 결정 순). */
type CpuDecision = Readonly<{ atMs: number; order: number; seatId: PlayerId }>

/**
 * `(from, to]` 구간에 넘은 CPU 판단 경계 전부를 **경계 시각 오름차순, 같은 시각은 좌석 순**으로. 경계 시각은 `intervalMs`의
 * 배수다(`cpuDecisionsDue`가 세는 것과 같은 경계 - 개수는 그 함수에서 받고 여기는 시각만 복원한다).
 */
function cpuDecisionsBetween(state: MatchState, fromMs: number, toMs: number): readonly CpuDecision[] {
  const decisions: CpuDecision[] = []
  seatsInOrder(state).forEach((seat, order) => {
    if (seat.kind !== 'cpu' || seat.bot === null) return
    const due = cpuDecisionsDue(fromMs, toMs, seat.bot)
    const firstBoundary = Math.floor(fromMs / seat.bot.intervalMs) + 1
    for (let n = 0; n < due; n += 1) {
      decisions.push({ atMs: (firstBoundary + n) * seat.bot.intervalMs, order, seatId: seat.id })
    }
  })
  return decisions.sort((a, b) => a.atMs - b.atMs || a.order - b.order)
}

function effectsFor(step: MatchStep, viewer: PlayerId): readonly MatchEffectBody[] {
  return step.effects.filter((effect) => effect.to === 'all' || effect.to === viewer).map((effect) => effect.body)
}

/** 메시지를 받을 좌석 - 사람만. 봇에게는 클라이언트가 없다. 끊긴 사람도 포함한다(배달 여부는 전송의 몫). */
function recipients(state: MatchState): readonly PlayerId[] {
  return seatsInOrder(state)
    .filter((player) => player.kind === 'human')
    .map((player) => player.id)
}

/* --- 입구 ------------------------------------------------------------------------ */

export function createMatchHost(options: MatchHostOptions): MatchHost {
  const { roomCode, seed, deps } = options
  const config = options.config ?? defaultMatchConfig(deps.tuning)
  const tokenFor = options.tokenFor ?? ((playerId: PlayerId): string => `loopback:${playerId}`)

  let state: MatchState | null = null
  let seq = FIRST_SEQ
  let lastClockAtMs: number | null = null
  /** 사람마다 마지막으로 보낸 `rooms`의 가시성. 같으면 다시 보내지 않는다(머리말). */
  const sentRooms = new Map<PlayerId, RoomsVisibility>()
  /** 봇의 라운드 스트림. `round`가 바뀌면 통째로 새로 만든다. */
  const botRngs = new Map<PlayerId, Rng>()
  let botRngRound = -1
  /** CPU를 마지막으로 돌린 수색 경과(머리말 "커서"). 페이즈가 바뀌면 0. */
  let cpuCursorMs = 0

  const queue: Queued[] = []
  let draining = false
  const senders: SendFn[] = []

  /* --- 보내기 --- */

  function emit(to: PlayerId, msg: ServerMessage): void {
    for (const send of senders) send(to, msg)
  }

  function nextSeq(): number {
    const value = seq
    seq += 1
    return value
  }

  function viewFor(current: MatchState, viewer: PlayerId): ClientView {
    return toClientView(current, viewer, deps)
  }

  /** 이 사람에게 `rooms`를 보내고 가시성을 기억한다. `force`는 `resync`·재접속 - 같아도 보낸다. */
  function sendRooms(current: MatchState, to: PlayerId, visibility: RoomsVisibility, force: boolean): void {
    const roomsChangedFor = force || !sameVisibility(sentRooms.get(to), visibility)
    if (!roomsChangedFor) return
    sentRooms.set(to, visibility)
    emit(to, { type: 'rooms', seq: nextSeq(), ...roomsPayload(current, visibility, to) })
  }

  /**
   * 한 걸음의 결과를 사람들에게. `rooms`(필요할 때만)가 `step`보다 먼저다 - `step`의 `warpTo`·`syncProxies`가
   * 도착할 때 그 방이 이미 손에 있어야 한다. `exclude`는 방금 앉은 사람(`welcome`으로 이미 받았다).
   */
  function broadcast(step: MatchStep, exclude: PlayerId | null = null): void {
    const roomsChanged = step.effects.some((effect) => effect.body.type === 'roomsChanged')
    for (const to of recipients(step.state)) {
      if (to === exclude) continue
      const view = viewFor(step.state, to)
      sendRooms(step.state, to, view.rooms, roomsChanged)
      emit(to, {
        type: 'step',
        seq: nextSeq(),
        atMs: step.state.clockMs,
        phaseElapsedMs: step.state.phaseElapsedMs,
        view,
        effects: effectsFor(step, to),
      })
    }
  }

  /* --- 적용 --- */

  /**
   * 리듀서가 낸 걸음을 받아들인다. 페이즈가 바뀌면 CPU 커서를 0으로 - 리듀서가 `phaseElapsedMs`를 0으로 돌린 것과 같은 순간이다.
   * `quiet`(효과 0)인 걸음을 알릴지는 부르는 쪽이 정한다: 이벤트는 알린다(`ready` 하나가 뷰를 바꾼다), 틱은 알리지 않는다 -
   * 시계만 흐른 걸음을 매 틱 `step`으로 보내면 그것이 곧 "틱마다 스냅샷"이고, 그 자리는 2Hz `clock`의 것이다(`protocol.ts` 머리말).
   */
  function commit(step: MatchStep, announceQuiet: boolean): MatchState {
    if (step.effects.some((effect) => effect.body.type === 'phaseEnter')) cpuCursorMs = 0
    if (step.state.phase === 'lobby') {
      // 로비에서 나간 좌석은 사라진다. 그 사람의 가시성 기억도 함께 - 다음에 같은 id가 앉으면 새 사람이다.
      for (const id of sentRooms.keys()) if (step.state.players[id] === undefined) sentRooms.delete(id)
    }
    if (step.effects.length > 0 || announceQuiet) broadcast(step)
    return step.state
  }

  /** 이벤트 하나를 리듀서에 넣는다. 무시(참조 동일 · 효과 0)면 사람의 요청일 때만 `rejected`. */
  function apply(current: MatchState, queued: Queued): MatchState {
    const step = reduceMatch(current, queued.ev, deps)
    if (step.state === current && step.effects.length === 0) {
      if (queued.origin !== undefined) {
        emit(queued.origin.from, { type: 'rejected', cseq: queued.origin.cseq, reason: 'unknown' })
      }
      return current
    }
    return commit(step, true)
  }

  /**
   * 시계를 `atMs`까지. 같은 시각은 넘긴다(리듀서도 참조 동일이다). 뒤로 가는 시계는 리듀서가 던진다 - 호스트의 버그이고 조용히
   * 접으면 그 뒤 모든 타이머가 거짓이 된다. 효과 없는 틱은 `step`을 내지 않는다(`commit` 주석).
   */
  function applyTick(current: MatchState, atMs: number): MatchState {
    if (atMs <= current.clockMs) return current
    return commit(reduceMatch(current, { type: 'tick', atMs }, deps), false)
  }

  /** 큐를 비운다. 비우는 도중 들어온 것(CPU의 연쇄)도 같은 루프에서 처리된다 - 순서가 곧 도착 순이다. */
  function drain(): void {
    if (draining || state === null) return
    draining = true
    try {
      for (let queued = queue.shift(); queued !== undefined; queued = queue.shift()) {
        if (state === null) break
        if (queued.atMs !== undefined) state = applyTick(state, queued.atMs)
        state = apply(state, queued)
      }
    } finally {
      draining = false
    }
  }

  function enqueue(queued: Queued): void {
    queue.push(queued)
    drain()
  }

  /* --- CPU --- */

  function botRngFor(current: MatchState, id: PlayerId): Rng {
    if (botRngRound !== current.round) {
      botRngs.clear()
      botRngRound = current.round
    }
    const existing = botRngs.get(id)
    if (existing !== undefined) return existing
    const rng = createRng(botStreamSeed(current.seed, id, current.round))
    botRngs.set(id, rng)
    return rng
  }

  /**
   * 이웃이 판단하는 페이즈(수색 · 바꾸기)에서 좌석마다 이 구간에 넘은 판단 경계만큼 `cpuDecide`를 부르고,
 * 결과를 **같은 큐**에 넣는다. 바꾸기가 포함된 이유는 M11이다 - 이웃도 자기 구획을 고치고 [다 했다]를 보낸다.
   * 판단 사이에 상태가 바뀌므로(앞 봇의 발견) 매 판단 직전의 `state`를 넘긴다. 수색이 끝나면 멈춘다 - 남은 판단은 버린다.
   *
   * **경계 시각 순으로 돈다, 좌석 순이 아니라.** 한 틱이 경계를 둘 이상 넘기면(탭 잠듦 · 큰 dt) 좌석 바깥 루프는 b1,b1,b2,b2
   * 순이 되고 잘게 온 틱은 b1,b2,b1,b2다 - 각 판단이 직전 상태(남의 발견)를 보므로 발견 목록이 갈린다. 같은 `?seed=`의
   * 재현이 프레임 열에 기대지 않으려면 적용 순서가 "언제 판단했는가"만의 함수여야 한다. 같은 경계 시각은 좌석 순이다.
   */
  function driveCpu(): void {
    if (state === null || !isCpuPhase(state)) return
    const from = cpuCursorMs
    const to = state.phaseElapsedMs
    cpuCursorMs = to
    for (const decision of cpuDecisionsBetween(state, from, to)) {
      if (state === null || !isCpuPhase(state)) return
      for (const ev of deps.cpuDecide(state, decision.seatId, botRngFor(state, decision.seatId))) {
        enqueue({ ev: { ...ev, by: decision.seatId } })
      }
    }
  }

  /* --- 시계 --- */

  /** 2Hz. 시간으로 끝나지 않는 페이즈(로비 · 결과)에는 보낼 시계가 없다. */
  function maybeClock(current: MatchState, atMs: number): void {
    if (phaseDurationMs(current, deps.tuning) === null) return
    if (lastClockAtMs !== null && atMs - lastClockAtMs < CLOCK_INTERVAL_MS) return
    lastClockAtMs = atMs
    for (const to of recipients(current)) emit(to, { type: 'clock', phaseElapsedMs: current.phaseElapsedMs })
  }

  /* --- 표면 --- */

  /** 이 사람에게 첫 화면 전부. 앉을 때와 돌아올 때 같은 메시지다 - 새 소켓은 둘 다 빈손이다. */
  function welcome(current: MatchState, to: PlayerId): void {
    const view = viewFor(current, to)
    sentRooms.set(to, view.rooms)
    emit(to, {
      type: 'welcome',
      playerId: to,
      token: tokenFor(to),
      roomCode,
      view,
      rooms: roomsPayload(current, view.rooms, to),
    })
  }

  function join(player: HostSeat, magazines: number): void {
    const ev: JoinEvent = { type: 'join', player, magazines }
    if (state === null) {
      state = createMatch(roomCode, seed, ev, config, deps)
    } else {
      const step = reduceMatch(state, ev, deps)
      if (step.state === state) return
      state = step.state
      // 새 좌석은 `welcome`으로 받는다. 나머지에게만 걸음을 알린다.
      if (player.kind === 'human') broadcast(step, player.id)
      else broadcast(step)
    }
    if (player.kind === 'human') welcome(state, player.id)
  }

  function receive(from: PlayerId, msg: ClientMessage, atMs: number): void {
    if (state === null || state.players[from] === undefined) {
      /*
       * 좌석이 없는 사람의 `act`. 도달 경로는 둘 다 로비 근처다 - 로비에서 소켓이 잠깐 끊겨 좌석이 지워진 뒤(`onLeave`)
       * 같은 소켓의 늦은 `act`가 오는 순간, 그리고 `in-progress`로 앉지 못한 소켓이 닫히기 전에 더 보내는 순간.
       * 조용히 버리면 그 번호를 기다리는 자리(사격 대기 줄 · 체인저 초안)가 영영 비지 않는다 - 코덱 실패에도 `rejected`가
       * 가는 것과 같은 이유다(`server/room.ts` 머리말). 사유는 `unknown` 하나다(머리말 - 게이트의 사본을 만들지 않는다).
       */
      if (msg.type === 'act') emit(from, { type: 'rejected', cseq: msg.cseq, reason: 'unknown' })
      return
    }
    switch (msg.type) {
      case 'act':
        // 코덱(`decodeClient`)이 이미 같은 검사를 했다. 다시 보는 이유는 `receive`가 코덱을 거치지 않은 호출자(테스트 ·
        // 서버 내부 · 위장 객체)에게도 열린 문이라서다 - 문은 두 겹이고, 한쪽을 지우면 다른 쪽이 유일한 방어선이 된다.
        if (!isPlayerEventType(msg.event.type)) {
          emit(from, { type: 'rejected', cseq: msg.cseq, reason: 'not-player-event' })
          return
        }
        enqueue({ ev: { ...msg.event, by: from }, atMs, origin: { from, cseq: msg.cseq } })
        return
      case 'resync': {
        state = applyTick(state, atMs)
        const view = viewFor(state, from)
        sendRooms(state, from, view.rooms, true)
        emit(from, {
          type: 'step',
          seq: nextSeq(),
          atMs: state.clockMs,
          phaseElapsedMs: state.phaseElapsedMs,
          view,
          effects: [],
        })
        return
      }
      case 'ping':
        emit(from, { type: 'pong', t: msg.t, serverMs: atMs })
        return
      // `hello`·`lobby`는 방 등록(누가 어느 방에 앉는가)의 일이라 서버(M6)가 자기 층에서 처리하고 `join`을 부른다.
      // 루프백은 좌석을 직접 앉힌다. `presence`의 팬아웃은 M8이다.
      case 'hello':
      case 'lobby':
      case 'presence':
        return
    }
  }

  return {
    roomCode,
    join,
    leave(playerId: PlayerId, atMs?: number): void {
      if (state === null) return
      if (atMs !== undefined) state = applyTick(state, atMs)
      state = apply(state, { ev: { type: 'leave', playerId } })
    },
    reconnect(playerId: PlayerId, atMs?: number): void {
      if (state === null || state.players[playerId] === undefined) return
      if (atMs !== undefined) state = applyTick(state, atMs)
      const step = reduceMatch(state, { type: 'reconnect', playerId }, deps)
      if (step.state !== state) {
        // 나머지에게 `connected`가 바뀐 걸음을. 돌아온 사람은 아래 `welcome` 하나로 받는다(두 벌이면 `seq` 없는 welcome 뒤에
        // 옛 step이 겹친다).
        state = step.state
        broadcast(step, playerId)
      }
      welcome(state, playerId)
    },
    receive,
    tick(atMs: number): void {
      if (state === null) return
      state = applyTick(state, atMs)
      driveCpu()
      if (state !== null) maybeClock(state, atMs)
    },
    onSend(send: SendFn): () => void {
      senders.push(send)
      return () => {
        const at = senders.indexOf(send)
        if (at >= 0) senders.splice(at, 1)
      }
    },
    state: () => state,
  }
}
